import { useState } from 'react'

import { type Attempt, type ExamCard, writeCardAnswer } from '../../api/exams'
import { asFailure, type Failure } from '../../api/request'
import { Failed } from '../../ui/Status'
import { useSaving } from '../../ui/useSaving'
import { Preview } from '../../ui/preview/Preview'
import { MARKDOWN } from '../../ui/markup'
import { RichText } from '../../ui/RichText'

/**
 * The answer side of a card-set task: one plain-text field per card, saved like everything else.
 *
 * Deliberately not a learning round: there is no reveal, no right or wrong, and nothing here
 * compares an answer to the card's own back — that happens afterwards, in an assistant's head. Each
 * card saves on its own, half a second after typing stops, exactly as the notepad and a written
 * answer do; switching tasks unmounts these fields, which flushes whatever was still pending.
 */
export interface CardAnswersProps {
  readonly attemptId: string
  readonly taskId: string
  readonly cards: readonly ExamCard[]
  /** What was written for each card so far, by card id, with the markup it is written in. */
  readonly answers: ReadonlyMap<string, { readonly text: string; readonly markup: string }>
  readonly onChanged: (attempt: Attempt) => void
}

export function CardAnswers({ attemptId, taskId, cards, answers, onChanged }: CardAnswersProps) {
  if (cards.length === 0) {
    return <p className="question__gone">This set names no card any more.</p>
  }

  return (
    <ol className="sitting__cards">
      {cards.map((card, index) => (
        <li key={card.id} className="sitting__card">
          {card.gone ? (
            <span className="question__gone">A card that is no longer there.</span>
          ) : (
            <CardAnswerField
              attemptId={attemptId}
              taskId={taskId}
              card={card}
              index={index}
              total={cards.length}
              answer={answers.get(card.id)}
              onChanged={onChanged}
            />
          )}
        </li>
      ))}
    </ol>
  )
}

function CardAnswerField({
  attemptId,
  taskId,
  card,
  index,
  total,
  answer,
  onChanged,
}: {
  readonly attemptId: string
  readonly taskId: string
  readonly card: ExamCard
  readonly index: number
  readonly total: number
  readonly answer?: { readonly text: string; readonly markup: string }
  readonly onChanged: (attempt: Attempt) => void
}) {
  const [refused, setRefused] = useState<Failure | null>(null)
  const [markup, setMarkup] = useState(answer?.markup ?? MARKDOWN)
  const label = `Your answer to card ${String(index + 1)} of ${String(total)}`

  const saving = useSaving(async (value: string) => {
    try {
      const written = await writeCardAnswer(attemptId, taskId, card.id, value, markup)
      setRefused(null)
      onChanged(written)
    } catch (error) {
      setRefused(asFailure(error))
    }
  })

  return (
    <>
      <Preview
        text={card.front}
        format={card.frontFormat}
        label={`Card ${String(index + 1)} of ${String(total)}`}
      />
      <div className="sitting__cardAnswer">
        <RichText
          compact
          text={answer?.text ?? ''}
          format={markup}
          label={label}
          purpose="exam-card-answer"
          initially="text"
          placeholder="Write your answer…"
          onChange={(value) => {
            saving.typed(value)
          }}
          onFormat={async (wanted) => {
            setMarkup(wanted)
            const written = await writeCardAnswer(
              attemptId,
              taskId,
              card.id,
              answer?.text ?? '',
              wanted,
            )
            onChanged(written)
          }}
          onSubmit={saving.now}
        />
      </div>
      {refused !== null && <Failed failure={refused} />}
    </>
  )
}
